import Phaser from "phaser";

export class VirtualDPad {
    private readonly scene: Phaser.Scene;
    private readonly deadzone = 0.16;
    private edgePadding = 190;
    private baseRadius = 62;
    private knobRadius = 30;
    private maxKnobDistance = 42;
    private baseHitRadius = 84;
    private knobHitRadius = 48;
    private controlZonePadding = 28;
    private center = new Phaser.Math.Vector2(0, 0);
    private direction = new Phaser.Math.Vector2(0, 0);
    private activePointerId: number | null = null;
    private base?: Phaser.GameObjects.Arc;
    private knob?: Phaser.GameObjects.Arc;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
    }

    create(): void {
        this.updateLayoutMetrics(this.scene.scale.width, this.scene.scale.height);

        const centerX = this.scene.scale.width - this.edgePadding;
        const centerY = this.scene.scale.height - this.edgePadding;
        this.center.set(centerX, centerY);

        this.base = this.scene.add
            .circle(centerX, centerY, this.baseRadius, 0xffffff, 0.2)
            .setStrokeStyle(3, 0xffffff, 0.46)
            .setScrollFactor(0)
            .setDepth(200)
            .setInteractive(new Phaser.Geom.Circle(0, 0, this.baseHitRadius), Phaser.Geom.Circle.Contains);

        this.knob = this.scene.add
            .circle(centerX, centerY, this.knobRadius, 0xffffff, 0.5)
            .setStrokeStyle(2, 0xffffff, 0.7)
            .setScrollFactor(0)
            .setDepth(201)
            .setInteractive(new Phaser.Geom.Circle(0, 0, this.knobHitRadius), Phaser.Geom.Circle.Contains);

        this.base.on("pointerdown", this.handlePointerDown, this);
        this.knob.on("pointerdown", this.handlePointerDown, this);

        this.scene.input.on("pointermove", this.handlePointerMove, this);
        this.scene.input.on("pointerup", this.handlePointerUp, this);
        this.scene.input.on("pointerupoutside", this.handlePointerUp, this);
        this.scene.scale.on("resize", this.handleResize, this);
    }

    getDirection(): Phaser.Math.Vector2 {
        return this.direction.clone();
    }

    ownsPointer(pointer: Phaser.Input.Pointer): boolean {
        return this.activePointerId === pointer.id;
    }

    isInControlZone(screenX: number, screenY: number): boolean {
        const distance = Phaser.Math.Distance.Between(screenX, screenY, this.center.x, this.center.y);
        return distance <= this.baseRadius + this.maxKnobDistance + this.controlZonePadding;
    }

    private handlePointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.activePointerId !== null && this.activePointerId !== pointer.id) {
            return;
        }

        this.activePointerId = pointer.id;
        this.updateFromPointer(pointer);
        this.knob?.setFillStyle(0xffffff, 0.62);
    }

    private handlePointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.activePointerId !== pointer.id) {
            return;
        }

        this.updateFromPointer(pointer);
    }

    private handlePointerUp(pointer: Phaser.Input.Pointer): void {
        if (this.activePointerId !== pointer.id) {
            return;
        }

        this.activePointerId = null;
        this.direction.set(0, 0);
        this.knob?.setPosition(this.center.x, this.center.y);
        this.knob?.setFillStyle(0xffffff, 0.45);
    }

    private updateFromPointer(pointer: Phaser.Input.Pointer): void {
        const deltaX = pointer.x - this.center.x;
        const deltaY = pointer.y - this.center.y;
        const distance = Math.hypot(deltaX, deltaY);
        const clampedDistance = Math.min(distance, this.maxKnobDistance);
        const angle = Math.atan2(deltaY, deltaX);
        const knobX = this.center.x + Math.cos(angle) * clampedDistance;
        const knobY = this.center.y + Math.sin(angle) * clampedDistance;

        this.knob?.setPosition(knobX, knobY);

        const magnitude = this.maxKnobDistance > 0 ? clampedDistance / this.maxKnobDistance : 0;
        if (magnitude <= this.deadzone) {
            this.direction.set(0, 0);
            return;
        }

        this.direction.set(Math.cos(angle), Math.sin(angle));
    }

    private handleResize(gameSize: Phaser.Structs.Size): void {
        this.updateLayoutMetrics(gameSize.width, gameSize.height);
        this.center.set(gameSize.width - this.edgePadding, gameSize.height - this.edgePadding);
        this.base?.setRadius(this.baseRadius);
        this.base?.setInteractive(new Phaser.Geom.Circle(0, 0, this.baseHitRadius), Phaser.Geom.Circle.Contains);

        this.knob?.setRadius(this.knobRadius);
        this.knob?.setInteractive(new Phaser.Geom.Circle(0, 0, this.knobHitRadius), Phaser.Geom.Circle.Contains);

        this.base?.setPosition(this.center.x, this.center.y);

        if (this.activePointerId === null) {
            this.knob?.setPosition(this.center.x, this.center.y);
        }
    }

    private updateLayoutMetrics(width: number, height: number): void {
        const shortSide = Math.min(width, height);
        const layoutScale = Phaser.Math.Clamp(shortSide / 1080, 0.68, 1);

        this.baseRadius = Math.round(62 * layoutScale);
        this.knobRadius = Math.round(30 * layoutScale);
        this.maxKnobDistance = Math.round(42 * layoutScale);
        this.edgePadding = Math.round(190 * layoutScale);
        this.baseHitRadius = this.baseRadius + Math.round(22 * layoutScale);
        this.knobHitRadius = this.knobRadius + Math.round(18 * layoutScale);
        this.controlZonePadding = Math.round(28 * layoutScale);
    }
}
